import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export { BACKEND_URL };
export const API = `${BACKEND_URL}/api`;
export const TOKEN_KEY = "gadopro_token";

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/* ---------- Cota de plano (402) ---------- */

/* Toast de bloqueio por cota com botão que leva direto à página de upgrade. */
export function showQuotaBlocked(message) {
  toast.error(message || "Limite do plano Grátis atingido", {
    duration: 8000,
    action: {
      label: "Assinar Pro",
      onClick: () => window.location.assign("/app/upgrade"),
    },
  });
}

/* Wrapper de toast para fluxos que podem estourar cota:
   402 → toast com botão "Assinar Pro"; demais erros → toast de erro comum. */
export async function toastWithQuota(promise, { loading, success, error }) {
  const id = toast.loading(loading);
  try {
    const r = await promise;
    toast.success(typeof success === "function" ? success(r) : success, { id });
    return r;
  } catch (e) {
    if (e?.code === 402) {
      toast.dismiss(id);
      showQuotaBlocked(e.message);
    } else {
      toast.error(typeof error === "function" ? error(e) : error, { id });
    }
    return undefined;
  }
}

export const ANIMAL_TYPES = [
  "Boi",
  "Vaca",
  "Garrote",
  "Novilha",
  "Bezerro",
  "Bezerra",
];

/* ---------------- Formatters (pt-BR) ---------------- */
export const formatBRL = (v) =>
  (v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

export const formatNum = (v, d = 2) =>
  (v || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

export const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

/* ---------------- Domain totals (mirrors backend) ---------------- */
export function computeTotals(payload) {
  const animals = payload.animals || [];
  const real = animals.filter((a) => a.weight > 0.1 && a.quantity > 0);
  const all = [...real];
  let totalDiscount = Number(payload.discount_kg) || 0;
  (payload.partial_weighings || []).forEach((pw) => {
    all.push(...(pw.animals || []));
    totalDiscount += Number(pw.discount_kg) || 0;
  });
  const total_quantity = all.reduce((s, a) => s + Number(a.quantity || 0), 0);
  const rawWeight = all.reduce((s, a) => s + Number(a.weight || 0), 0);
  const total_weight = Math.max(rawWeight - totalDiscount, 0);
  const total_arrobas = total_weight / 30;
  const avg_weight = total_quantity > 0 ? total_weight / total_quantity : 0;
  const avg_arrobas = total_quantity > 0 ? total_arrobas / total_quantity : 0;
  const total_amount = total_arrobas * (Number(payload.price_per_arroba) || 0);
  const unit_price_per_animal =
    total_quantity > 0 ? total_amount / total_quantity : 0;
  return {
    total_quantity,
    total_weight,
    total_arrobas,
    avg_weight,
    avg_arrobas,
    total_amount,
    unit_price_per_animal,
  };
}

/* Download the monthly consolidated report (Pro). */
export async function downloadMonthlyReport(year, month) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API}/reports/monthly?year=${year}&month=${month}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 402) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail || "Recurso exclusivo do plano Pro");
    err.code = 402;
    throw err;
  }
  if (!res.ok) throw new Error("Falha ao gerar relatório");
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Relatorio_Mensal_${String(month).padStart(2, "0")}-${year}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/* Compute totals for a single partial weighing block. */
export function partialTotals(animals, discount_kg) {
  const real = (animals || []).filter((a) => a.weight > 0.1 && a.quantity > 0);
  const total_quantity = real.reduce((s, a) => s + Number(a.quantity || 0), 0);
  const raw = real.reduce((s, a) => s + Number(a.weight || 0), 0);
  const total_weight = Math.max(raw - (Number(discount_kg) || 0), 0);
  const total_arrobas = total_weight / 30;
  const avg_weight = total_quantity > 0 ? total_weight / total_quantity : 0;
  const avg_arrobas = total_quantity > 0 ? total_arrobas / total_quantity : 0;
  return { total_quantity, total_weight, total_arrobas, avg_weight, avg_arrobas };
}

/* Download an individual transaction PDF (auth via bearer). */
export async function downloadPdf(transactionId, filename) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API}/transactions/${transactionId}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 402) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail || "Limite do plano gratuito atingido");
    err.code = 402;
    throw err;
  }
  if (!res.ok) throw new Error("Falha ao gerar PDF");
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "pesagem.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/* Start Stripe checkout for a plan; returns checkout_url. */
export async function startCheckout(lookupKey) {
  const { data } = await api.post("/payments/checkout", {
    lookup_key: lookupKey,
    origin_url: window.location.origin,
  });
  return data.checkout_url;
}

/* Share the transaction PDF via Web Share API (native share sheet).
   Falls back to download when the platform can't share files. */
export async function shareTransactionPdf(tx) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API}/transactions/${tx.transaction_id}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 402) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail || "Limite do plano gratuito atingido");
    err.code = 402;
    throw err;
  }
  if (!res.ok) throw new Error("Falha ao gerar PDF");
  const blob = await res.blob();
  const dateStr = tx.date ? formatDate(tx.date).replace(/\//g, "-") : "";
  const filename = `Pesagem_${(tx.buyer_name || "Gado").replace(/\s+/g, "_")}_${dateStr}.pdf`;
  const file = new File([blob], filename, { type: "application/pdf" });
  const shareData = {
    files: [file],
    title: "GadoPro — Pesagem",
    text: `${tx.buyer_name || "Pesagem"} · ${tx.total_quantity || 0} cab. · ${formatBRL(tx.total_amount || 0)}`,
  };
  if (navigator.canShare?.(shareData) && navigator.share) {
    try {
      await navigator.share(shareData);
      return { shared: true };
    } catch (e) {
      if (e?.name === "AbortError") return { shared: false, cancelled: true };
      /* fall through to download */
    }
  }
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return { shared: false, downloaded: true };
}

/* Share the AI analysis PDF via native share sheet.
   Also attaches the video keyframe thumbnail so WhatsApp shows the cattle in the preview.
   Fallback: download the PDF locally. */
export async function shareAnalysisPdf(analysis) {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = { Authorization: `Bearer ${token}` };
  // Fetch PDF and thumbnail in parallel (thumb is optional — older analyses may not have one)
  const [pdfRes, thumbRes] = await Promise.all([
    fetch(`${API}/analyses/${analysis.analysis_id}/pdf`, { headers }),
    fetch(`${API}/analyses/${analysis.analysis_id}/thumbnail`, { headers }).catch(() => null),
  ]);
  if (!pdfRes.ok) throw new Error("Falha ao gerar PDF");
  const pdfBlob = await pdfRes.blob();
  const shortId = (analysis.analysis_id || "").slice(-6).toUpperCase();
  const pdfName = `Analise_IA_${shortId}.pdf`;
  const files = [];
  if (thumbRes && thumbRes.ok) {
    const thumbBlob = await thumbRes.blob();
    files.push(new File([thumbBlob], `Analise_IA_${shortId}.jpg`, { type: "image/jpeg" }));
  }
  files.push(new File([pdfBlob], pdfName, { type: "application/pdf" }));
  const r = analysis.result || {};
  const shareData = {
    files,
    title: "GadoPro — Análise por IA",
    text: `${r.total_animals || 0} cabeças · ${r.breed_guess || "—"} · ${Math.round(r.total_weight_kg || 0)} kg (estimado)`,
  };
  if (navigator.canShare?.(shareData) && navigator.share) {
    try {
      await navigator.share(shareData);
      return { shared: true };
    } catch (e) {
      if (e?.name === "AbortError") return { shared: false, cancelled: true };
    }
  }
  // Fallback — download the PDF (thumbnail is nice-to-have when share is unavailable)
  const url = window.URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = pdfName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return { shared: false, downloaded: true };
}

/* Download the branded PDF report for an AI video analysis. */
export async function downloadAnalysisPdf(analysisId, filename) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API}/analyses/${analysisId}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 402) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail || "Limite do plano gratuito atingido");
    err.code = 402;
    throw err;
  }
  if (!res.ok) throw new Error("Falha ao gerar PDF");
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `Analise_IA_${analysisId.slice(-6).toUpperCase()}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/* Share a branded PNG summary via Web Share API (fallback: download). */
export async function shareTransactionImage(tx) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API}/transactions/${tx.transaction_id}/share-image`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Falha ao gerar imagem");
  const blob = await res.blob();
  const filename = `GadoPro_${(tx.buyer_name || "Pesagem").replace(/\s+/g, "_")}.png`;
  const file = new File([blob], filename, { type: "image/png" });
  const shareData = {
    files: [file],
    title: "GadoPro — Pesagem",
    text: `${tx.buyer_name || "Pesagem"} · ${tx.total_quantity || 0} cab.`,
  };
  if (navigator.canShare?.(shareData) && navigator.share) {
    try {
      await navigator.share(shareData);
      return { shared: true };
    } catch {
      /* user cancelled or share failed → fall back to download */
    }
  }
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return { shared: false, downloaded: true };
}

// ---------- Avatar helpers (shared by Dashboard & Historico cards) ----------
export function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "•";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

const AVATAR_PALETTE = [
  "bg-gado-green/12 text-gado-green",
  "bg-gado-gold/15 text-gado-gold",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-teal-100 text-teal-700",
  "bg-orange-100 text-orange-700",
];

export function paletteFor(name) {
  const s = name || "?";
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i)) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[h];
}
